import Footer from '../../components/Footer'
import { useState, useEffect } from 'react'

export default function RegistrationGuidelines() {
  const [nationality, setNationality] = useState('indian'); // indian | international
  const [role, setRole] = useState('student'); // student | professional
  const [isIeee, setIsIeee] = useState(true); // true | false
  const [attendance, setAttendance] = useState('author'); // author | listener | delegate
  const [preconf, setPreconf] = useState(true); // true | false
  const [registrationType, setRegistrationType] = useState('late'); // early | normal | late
  const [numberOfPages, setNumberOfPages] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [paperId, setPaperId] = useState('');
  const [paperTitle, setPaperTitle] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | submitting | success | error
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');

  const GOOGLE_SHEET_URL = process.env.REACT_APP_GOOGLE_SHEET_URL || '';

  // Automatically determine registration type based on date
  useEffect(() => {
    const now = new Date();
    const earlyEnd = new Date('2025-10-25');
    const normalEnd = new Date('2025-10-31');
    const lateStart = new Date('2025-11-01');
    
    if (now <= earlyEnd) {
      setRegistrationType('early');
    } else if (now < lateStart) {
      setRegistrationType('normal');
    } else {
      setRegistrationType('late');
    }
  }, []);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      if (errors.uploadedFile) setErrors(prev => ({ ...prev, uploadedFile: undefined }));
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setUploadedFile(file);
  }

  function validateForm() {
    const newErrors = {};
    if (!paperId.trim()) newErrors.paperId = 'Paper ID is required';
    if (!paperTitle.trim()) newErrors.paperTitle = 'Paper Title is required';
    if (!authorName.trim()) newErrors.authorName = 'Author Name is required';
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) newErrors.email = 'Enter a valid email address';
    }
    if (!phone.trim()) {
      newErrors.phone = 'Phone Number is required';
    } else {
      const phoneDigits = phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) newErrors.phone = 'Enter a valid phone number';
    }
    const pagesNum = Number(numberOfPages);
    if (numberOfPages === '' || Number.isNaN(pagesNum)) {
      newErrors.numberOfPages = 'Enter number of pages';
    } else if (pagesNum < 1 || pagesNum > 12) {
      newErrors.numberOfPages = 'Pages must be between 1 and 12';
    }
    if (!uploadedFile) newErrors.uploadedFile = 'Payment screenshot is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleSubmit() {
    if (!validateForm()) return;
    if (!GOOGLE_SHEET_URL) {
      setSubmitStatus('error');
      setSubmitError('Missing REACT_APP_GOOGLE_SHEET_URL');
      return;
    }

    try {
      setSubmitStatus('submitting');
      setSubmitError('');

      let paymentScreenshotBase64 = '';
      let paymentScreenshotName = '';
      if (uploadedFile) {
        paymentScreenshotName = uploadedFile.name;
        const base64 = await fileToBase64(uploadedFile);
        paymentScreenshotBase64 = String(base64);
      }

      // Use FormData to avoid CORS preflight
      const formData = new FormData();
      formData.append('paperId', paperId);
      formData.append('paperTitle', paperTitle);
      formData.append('authorName', authorName);
      formData.append('email', email);
      formData.append('phone', phone);
      formData.append('nationality', nationality);
      formData.append('role', role);
      formData.append('isIeee', String(isIeee));
      formData.append('attendance', attendance);
      formData.append('preconf', String(preconf));
      formData.append('numberOfPages', String(numberOfPages));
      formData.append('registrationType', registrationType);
      formData.append('base', String(fees.base));
      formData.append('adjustment', String(fees.adjustment));
      formData.append('pageFee', String(fees.pageFee || 0));
      formData.append('total', String(fees.total));
      formData.append('paymentScreenshotName', paymentScreenshotName);
      formData.append('paymentScreenshotBase64', paymentScreenshotBase64);
      formData.append('submittedAt', new Date().toISOString());

      const res = await fetch(GOOGLE_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      });

      // In no-cors mode we can't read status; assume success if no network error

      setSubmitMessage(`Thank you! Your submission${paperId ? ` (Paper ID: ${paperId})` : ''} was received.`);
      // Reset form fields after successful submission
      resetForm();
      setSubmitStatus('success');
    } catch (err) {
      setSubmitStatus('error');
      setSubmitError(err?.message || 'Unknown error');
    }
  }

  function resetForm() {
    // Left column selections
    setNationality('indian');
    setRole('student');
    setIsIeee(true);
    setAttendance('author');
    setPreconf(true);
    setNumberOfPages('');

    // Right column inputs
    setPaperId('');
    setPaperTitle('');
    setAuthorName('');
    setEmail('');
    setPhone('');
    setUploadedFile(null);
    setDragActive(false);
    setErrors({});
  }

  const computeFees = () => {
    const IN = {
      conferenceOnly: {
        student: { ieee: 6000, non: 7000 },
        professional: { ieee: 7000, non: 8000 },
      },
    };

    const INTL = {
      conferenceOnly: {
        student: { ieee: 150, non: 200 },
        professional: { ieee: 200, non: 250 },
      },
    };

    const isIndian = nationality === 'indian';
    const table = isIndian ? IN : INTL;
    const mem = isIeee ? 'ieee' : 'non';

    let base = 0;
    base = table.conferenceOnly[role][mem];

    // Apply registration type adjustments
    let adjustment = 0;
    if (registrationType === 'early') {
      // Early-Bird discount
      adjustment = isIndian ? -1000 : -25;
    } else if (registrationType === 'normal') {
      // Normal registration uses base price (no adjustment)
      adjustment = 0;
    } else if (registrationType === 'late') {
      // Late registration penalty
      adjustment = isIndian ? 1000 : 50;
    }

    // Additional page charges (only after 6 pages)
    const pagesNumeric = Number(numberOfPages);
    const normalizedPages = !Number.isNaN(pagesNumeric) && pagesNumeric > 0 ? pagesNumeric : 1;
    const clampedPages = Math.max(1, Math.min(12, normalizedPages));
    const extraPages = Math.max(0, clampedPages - 6); // extra page fee still applies only after 6 pages
    const pageFee = extraPages * (isIndian ? 500 : 20);

    // Pre-conference workshop fee (+₹500 Indian, +$20 International when selected)
    const preconfFee = preconf ? (isIndian ? 500 : 20) : 0;

    const total = base + adjustment + pageFee + preconfFee;
    return { base, adjustment, pageFee, preconfFee, extraPages, total, isIndian, registrationType };
  };

  const fees = computeFees();
  return (
    <main>
      <div className="">
        <div className=" text-center ">
          <p className=" sm:text-4xl text-2xl font-bold pt-10 pb-5 uppercase  ">Registration Guidelines</p>
        </div>

        <div className=" sm:mx-auto mx-5  sm:max-w-xl lg:max-w-3xl xl:max-w-7xl gap-5 flex flex-col justify-center mt-5  text-justify">
          <p className="  text-lg sm:text-xl">
            At least one of the authors of each accepted paper must register for
            the conference for the paper to be included in the conference
            proceedings.
          </p>
          <p className="  text-lg sm:text-xl text-red-500 font-medium">
            All accepted and presented papers of AECTSD 2025 will be forwarded for possible inclusion in the IEEE Xplore digital library
            {/* All accepted and presented papers of AECTSD 2025 will be submitted
            for possible publication in the Conference proceedings. */}
          </p>
          <p className="  text-lg sm:text-xl">
            Full registration includes the registration of one paper. Additional
            papers for a single registration come with an additional fee. The
            maximum length of the paper is 6 pages including figures, tables,
            and references.
          </p>
          <p className="  text-lg sm:text-xl">
            Registration fee covers admission to all
            sessions, cost of publishing the article in conference proceedings, welcome reception, conference kit,
            refreshments, working lunch, banquet dinner and half-a-day tour to
            nearby places.
          </p>
          <p className="  text-lg sm:text-xl">
            A fee of Rs. 500 will be applied for each additional page (with a
            maximum of 2 pages).
          </p>
        </div>

        <div className="flex justify-center items-center py-10">
          <div className="overflow-x-auto  rounded-lg  bg-white p-5">
            <h2 className="sm:text-2xl text-lg font-bold text-center mb-6">
              Indian Authors:
            </h2>
            <table className="table-auto w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-gray-300 px-4 py-2"></th>
                  <th
                    colSpan="2"
                    className="border border-gray-300 px-4 py-2 text-center"
                  >
                    Graduate Student/Research Scholar
                  </th>
                  <th
                    colSpan="2"
                    className="border border-gray-300 px-4 py-2 text-center"
                  >
                    Professionals
                  </th>
                </tr>
                <tr className="bg-gray-200">
                  <th className="border border-gray-300 px-4 py-2">
                    Categories
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    IEEE Member (INR)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    Non-IEEE Member (INR)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    IEEE Member (INR)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    Non-IEEE Member (INR)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Conference only
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    6000*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    7000*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    7000*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    8000*
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Tutorial only
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    1000
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    1250
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    1250
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    1500
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Conference plus Tutorial
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    6500*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    7500*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    7500*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    8500*
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Indian Non-Author Attendee
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    3500
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    5000
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    4500
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    6000
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Rate per Additional Paper
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    3000
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    3000
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    3000
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    3000
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Extra Page (after 6 pages)
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    500
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    500
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    500
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    500
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>



        <div className=" sm:mx-auto mx-5  sm:max-w-xl lg:max-w-3xl xl:max-w-7xl gap-5 flex flex-col justify-center  text-justify">

          <div className=" ">
            <p className="  text-lg sm:text-xl font-semibold">*Early Bird Registration</p>
            <div className=" my-3 sm:text-lg">
              <p>
                1. Discount of INR 1000 on the conference and conference plus
                tutorial registration fee.
              </p>
              <p>2. Discount of INR 500 on the Indian non-author attendee fee</p>
            </div>
          </div>

          <div>
            <p className="   text-lg sm:text-xl font-semibold">*Late Registration Fee</p>
            <p className=" my-3 sm:text-lg">
              Additional fee of INR 1000 on the conference and conference plus
              tutorial registration fee.
            </p>
          </div>

          <div>
            <p className="   text-lg sm:text-xl font-semibold">Virtual Mode</p>
            <p className="my-3 text-lg">Additional fee of INR 1000 on the conference registration fee.</p>
          </div>
        </div>

        <div className="flex justify-center items-center py-10">
          <div className="overflow-x-auto  rounded-lg  bg-white p-5">
            <h2 className="sm:text-2xl text-lg font-bold text-center mb-6">
              Foreign Authors: 
            </h2>
            <table className="table-auto w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-gray-300 px-4 py-2"></th>
                  <th
                    colSpan="2"
                    className="border border-gray-300 px-4 py-2 text-center"
                  >
                    Graduate Student/Research Scholar
                  </th>
                  <th
                    colSpan="2"
                    className="border border-gray-300 px-4 py-2 text-center"
                  >
                    Professionals
                  </th>
                </tr>
                <tr className="bg-gray-200">
                  <th className="border border-gray-300 px-4 py-2">
                    Categories
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    IEEE Member (USD)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    Non-IEEE Member (USD)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    IEEE Member (USD)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    Non-IEEE Member (USD)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Conference only
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    150*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    200*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    200*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    250*
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Tutorial only
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    40
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    50
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    50
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    75
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Conference plus Tutorial
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    175*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    225*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    225*
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    300*
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Rate per Additional Paper
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    50
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    50
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    50
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    50
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2 text-left">
                    Extra Page (after 6 pages)
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    20
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    20
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    20
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    20
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="  sm:mx-auto mx-5  sm:max-w-xl lg:max-w-3xl xl:max-w-7xl gap-5 flex flex-col justify-center  mb-20  text-justify">


          <div className=''>
            <p className=" text-lg sm:text-xl font-semibold ">*Early-Bird Registration</p>
            <p className=" my-3 sm:text-lg">
              Discount of USD 25 on the conference and conference plus tutorial
              registration fee.
            </p>
          </div>
          <div>
            <p className="  text-lg sm:text-xl font-semibold">*Late Registration fee:</p>
            <p className=" my-3 sm:text-lg">
              Additional fee of USD 25 on the conference and conference plus
              tutorial registration fee.
            </p>
          </div>
          <div>
            <p className="  text-lg sm:text-xl font-semibold">Virtual Mode</p>
            <p className=" my-3 sm:text-lg">Additional fee of USD 25 on the conference registration fee.</p>
          </div>
        </div>

        {/* Calculate Fees - Interactive Form (moved to bottom) */}
        <div className="sm:mx-auto mx-5 sm:max-w-2xl lg:max-w-5xl xl:max-w-7xl my-10 p-5 sm:p-6 bg-white rounded-lg shadow">
          <h2 className="text-center text-lg font-semibold text-blue-600 mb-1">
            Registration Fee Calculator - {
              registrationType === 'early' 
                ? 'Early-Bird (Valid until: October 25, 2025)'
                : registrationType === 'normal'
                ? 'Normal (From: October 26, 2025 to October 31, 2025)'
                : 'Late (From: November 1, 2025)'
            }
          </h2>
          <p className="text-center text-sm text-gray-600 mb-4">
            {registrationType === 'early' 
              ? `Discount: ${nationality === 'indian' ? '₹1000' : '$25'}`
              : registrationType === 'normal'
              ? `Normal Registration`
              : `Penalty: ${nationality === 'indian' ? '₹1000' : '$50'}`}
          </p>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Calculate Fees</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Column 1: Calculator */}
            <div>
              <p className="mb-4">Select particular options applicable to you to calculate registration fees.</p>

              {/* Registration Type - Auto-determined by date */}
              <div className="mb-5">
                <p className="font-semibold mb-2">Registration Type*</p>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${
                      registrationType === 'early' ? 'bg-green-500' 
                      : registrationType === 'normal' ? 'bg-orange-500' 
                      : 'bg-red-500'
                    }`}></div>
                    <span className="font-medium">
                      {registrationType === 'early' 
                        ? 'Early-Bird Registration' 
                        : registrationType === 'normal'
                        ? 'Normal Registration'
                        : 'Late Registration'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {registrationType === 'early' 
                      ? `Valid until: October 25, 2025 (Discount: ${nationality === 'indian' ? '₹1000' : '$25'})`
                      : registrationType === 'normal'
                      ? `From: October 26, 2025 to October 31, 2025`
                      : `From: November 1, 2025 (Penalty: ${nationality === 'indian' ? '₹1000' : '$50'})`
                    }
                  </p>
                </div>
              </div>

              {/* Nationality */}
              <div className="mb-5">
                <p className="font-semibold mb-2">Are you Indian or International?*</p>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="nat" checked={nationality === 'indian'} onChange={() => setNationality('indian')} />
                    <span>Indian</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="nat" checked={nationality === 'international'} onChange={() => setNationality('international')} />
                    <span>International</span>
                  </label>
                </div>
              </div>

              {/* Role */}
              <div className="mb-5">
                <p className="font-semibold mb-2">Are you a student or a professional?*</p>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="role" checked={role === 'student'} onChange={() => setRole('student')} />
                    <span>Student</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="role" checked={role === 'professional'} onChange={() => setRole('professional')} />
                    <span>Professional</span>
                  </label>
                </div>
              </div>

              {/* IEEE */}
              <div className="mb-5">
                <p className="font-semibold mb-2">Are you an IEEE member?*</p>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="ieee" checked={isIeee === true} onChange={() => setIsIeee(true)} />
                    <span>Yes</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="ieee" checked={isIeee === false} onChange={() => setIsIeee(false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {/* Attendance */}
              <div className="mb-5">
                <p className="font-semibold mb-2">Will you be attending as an author, a listener or delegate?*</p>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="att" checked={attendance === 'author'} onChange={() => setAttendance('author')} />
                    <span>Author/Presenter</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="att" checked={attendance === 'listener'} onChange={() => setAttendance('listener')} />
                    <span>Listener (Non Author/Attendee/Non-Presenter/Companion Participant)</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="att" checked={attendance === 'delegate'} onChange={() => setAttendance('delegate')} />
                    <span>Delegates from Industry/Practitioners</span>
                  </label>
                </div>
              </div>

              {/* Pre-conference */}
              <div className="mb-6">
                <p className="font-semibold mb-2">Will you attend pre-conference workshop?*</p>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="pre" checked={preconf === true} onChange={() => setPreconf(true)} />
                    <span>Yes</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="pre" checked={preconf === false} onChange={() => setPreconf(false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {/* Number of Pages */}
              <div className="mb-6">
                <label className="font-semibold mb-2 block">Number of Pages</label>
                <input
                  type="text"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400 max-w-xs ${errors.numberOfPages ? 'border-red-500' : ''}`}
                  value={numberOfPages}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow only numbers and empty string
                    if (value === '' || /^\d+$/.test(value)) {
                      setNumberOfPages(value);
                      if (errors.numberOfPages) {
                        const v = Number(value);
                        if (value !== '' && v >= 1 && v <= 12) setErrors(prev => ({ ...prev, numberOfPages: undefined }));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') return; // keep empty; don't auto-fill
                    const v = Number(e.target.value);
                    if (Number.isNaN(v) || v < 1 || v > 12) {
                      setErrors(prev => ({ ...prev, numberOfPages: 'Pages must be between 1 and 12' }));
                    } else if (errors.numberOfPages) {
                      setErrors(prev => ({ ...prev, numberOfPages: undefined }));
                    }
                  }}
                  placeholder="Enter number of pages (1-12)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum: 1 page, Maximum: 12 pages
                </p>
                {errors.numberOfPages && (
                  <p className="text-xs text-red-500 mt-1">{errors.numberOfPages}</p>
                )}
                {numberOfPages && numberOfPages < 1 && (
                  <p className="text-xs text-red-500 mt-1">
                    ⚠️ Minimum 1 page required
                  </p>
                )}
                {numberOfPages && numberOfPages > 12 && (
                  <p className="text-xs text-red-500 mt-1">
                    ⚠️ Maximum 12 pages allowed
                  </p>
                )}
              </div>

              {/* Totals */}
              <div className="pt-2 border-t">
                <p className="mt-3 font-semibold">Base Fees: {fees.isIndian ? `₹${fees.base}` : `$${fees.base}`}</p>
                {fees.adjustment < 0 && (
                  <p className="mt-1 text-sm">
                    Early-Bird Discount: 
                    {fees.isIndian ? `₹${Math.abs(fees.adjustment)}` : `$${Math.abs(fees.adjustment)}`}
                  </p>
                )}
                {fees.adjustment > 0 && (
                  <p className="mt-1 text-sm text-red-600">
                    Late Registration Penalty: 
                    {fees.isIndian ? `₹${fees.adjustment}` : `$${fees.adjustment}`}
                  </p>
                )}
                {fees.extraPages > 0 && (
                  <p className="mt-1 text-sm">
                    Extra Pages ({fees.extraPages} @ {fees.isIndian ? '₹500' : '$20'}): {fees.isIndian ? `₹${fees.pageFee}` : `$${fees.pageFee}`}
                  </p>
                )}
                {preconf && (
                  <p className="mt-1 text-sm">Pre-conference Workshop: {fees.isIndian ? `₹${fees.preconfFee}` : `$${fees.preconfFee}`}</p>
                )}
                <p className="mt-1 text-xl font-extrabold">Total Fees: {fees.isIndian ? `₹${fees.total}` : `$${fees.total}`}</p>
              </div>
            </div>

            {/* Column 2: Bank Account Details */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Bank Account Details</h3>
              <div className="rounded-lg border p-4 sm:p-5 bg-gray-50">
                <div className="flex flex-col gap-3 text-sm sm:text-base">
                  <div className="flex justify-between gap-4"><span className="font-semibold">Account Name</span><span className="text-right">Sri Ramakrishna Engineering College - AECTSD</span></div>
                  <div className="flex justify-between gap-4"><span className="font-semibold">Account Number</span><span className="text-right break-all">058705008310</span></div>
                  <div className="flex justify-between gap-4"><span className="font-semibold">Account Type</span><span className="text-right">Current</span></div>
                  <div className="flex justify-between gap-4"><span className="font-semibold">Customer ID</span><span className="text-right">602339485</span></div>
                  <div className="flex justify-between gap-4"><span className="font-semibold">IFSC Code</span><span className="text-right">ICIC0000587</span></div>
                </div>
                <p className="mt-4 text-xs text-gray-600">Note: Please include your Paper ID in the payment reference.</p>
                {/* Author details form */}
                <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

                  <div>
                    <label className="block text-sm font-medium mb-1">Paper ID</label>
                    <input
                      type="text"
                      className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400 ${errors.paperId ? 'border-red-500' : ''}`}
                      placeholder="Enter paper ID"
                      value={paperId}
                      onChange={(e) => {
                        setPaperId(e.target.value);
                        if (errors.paperId && e.target.value.trim()) setErrors(prev => ({ ...prev, paperId: undefined }));
                      }}
                    />
                    {errors.paperId && <p className="text-xs text-red-500 mt-1">{errors.paperId}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Paper Title</label>
                    <input
                      type="text"
                      className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400 ${errors.paperTitle ? 'border-red-500' : ''}`}
                      placeholder="Enter paper title"
                      value={paperTitle}
                      onChange={(e) => {
                        setPaperTitle(e.target.value);
                        if (errors.paperTitle && e.target.value.trim()) setErrors(prev => ({ ...prev, paperTitle: undefined }));
                      }}
                    />
                    {errors.paperTitle && <p className="text-xs text-red-500 mt-1">{errors.paperTitle}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Author Name</label>
                    <input
                      type="text"
                      className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400 ${errors.authorName ? 'border-red-500' : ''}`}
                      placeholder="Enter author name"
                      value={authorName}
                      onChange={(e) => {
                        setAuthorName(e.target.value);
                        if (errors.authorName && e.target.value.trim()) setErrors(prev => ({ ...prev, authorName: undefined }));
                      }}
                    />
                    {errors.authorName && <p className="text-xs text-red-500 mt-1">{errors.authorName}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input
                        type="email"
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400 ${errors.email ? 'border-red-500' : ''}`}
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) {
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (emailRegex.test(e.target.value)) setErrors(prev => ({ ...prev, email: undefined }));
                          }
                        }}
                      />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone Number</label>
                      <input
                        type="tel"
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400 ${errors.phone ? 'border-red-500' : ''}`}
                        placeholder="+91-XXXXXXXXXX"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (errors.phone) {
                            const phoneDigits = e.target.value.replace(/\D/g, '');
                            if (phoneDigits.length >= 10) setErrors(prev => ({ ...prev, phone: undefined }));
                          }
                        }}
                      />
                      {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                    </div>
                  </div>
                  {/* Drag & drop upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Upload Payment Screenshot</label>
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`flex flex-col items-center justify-center border-2 border-dashed rounded-md px-4 py-8 text-center cursor-pointer ${dragActive ? 'border-primary-blue bg-blue-50' : 'border-gray-300 bg-white'}`}
                      onClick={() => document.getElementById('payment-upload-input')?.click()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h10a4 4 0 004-4M7 10l5-5m0 0l5 5m-5-5v12" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">Drag and drop file here, or click to browse</p>
                      <p className="text-xs text-gray-500">Accepted: PNG, JPG, PDF (max 10MB)</p>
                      {uploadedFile && (
                        <p className="mt-2 text-sm font-medium text-gray-700">Selected: {uploadedFile.name}</p>
                      )}
                      <input id="payment-upload-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFileSelect} />
                      {errors.uploadedFile && <p className="text-xs text-red-500 mt-1">{errors.uploadedFile}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={submitStatus === 'submitting'}
                      className={`mt-2 inline-flex items-center justify-center px-4 py-2 rounded text-white ${submitStatus === 'submitting' ? 'bg-gray-400' : 'bg-primary-blue hover:opacity-90'}`}
                    >
                      {submitStatus === 'submitting' ? 'Submitting…' : 'Submit'}
                    </button>
                  </div>
                </form>

                {/* Submission popup modal */}
                {submitStatus === 'success' && (
                  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-4 text-center">
                      <h3 className="text-lg font-semibold mb-2">Submission Successful</h3>
                      <p className="text-gray-700 mb-4">{submitMessage || 'Your details have been submitted successfully.'}</p>
                      <button
                        className="bg-primary-blue text-white px-4 py-2 rounded hover:opacity-90"
                        onClick={() => setSubmitStatus('idle')}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-4 text-center">
                      <h3 className="text-lg font-semibold mb-2 text-red-600">Submission Failed</h3>
                      <p className="text-gray-700 mb-4">{submitError || 'There was a problem submitting your form. Please try again.'}</p>
                      <button
                        className="bg-gray-700 text-white px-4 py-2 rounded hover:opacity-90"
                        onClick={() => setSubmitStatus('idle')}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      <Footer />
    </main>
  );
}
